/**
 * 认证相关 API (烘干厂端)
 *
 * 对接后端:
 *   - POST /sys/login                              登录
 *   - GET  /sys/permission/getUserPermissionByToken 拉取当前用户菜单+按钮权限 (登录后立即调)
 *   - POST /sys/user/change-password               修改密码 (需 Token)
 *   - POST /sys/pwd-reset-request                  申请重置密码 (匿名)
 *   - POST /sys/logout                             退出登录
 *
 * 错误码: 后端用 message 前缀字符串嵌入, 形如 "[LOGIN_011] xxx" / "[PWD_001] xxx",
 *        业务层用 err.message.startsWith('[LOGIN_011]') 判断.
 */
import request from '@/utils/request'

/**
 * 登录
 * @param {Object} data
 * @param {string} data.username  用户名 / 手机号
 * @param {string} data.password  密码 (明文)
 * @returns {Promise<{token, userInfo, isExternal, forceChangePwd, externalOrg, permissions, menus}>}
 *
 * T-013: 登录接口单独放宽到 60s
 * 理由: jeecg-boot 冷启动 (Shiro 缓存 / Redis 连接池 / 用户菜单 queryByUser 多表 JOIN) 首次可能 >15s,
 * 全局默认 15s 太短, 用户体验是"前两次超时, 第三次才进得去". 登录是一次性高耗操作, 放宽到 60s 不影响其他接口.
 */
export function login(data) {
  return request.post('/jeecg-system/sys/login', data, { timeout: 60000 })
}

/**
 * 拉取当前登录用户的菜单与按钮权限
 *
 * 后端 SysPermissionController.getUserPermissionByToken:
 *   - 通过 SQL queryByUser(loginAcct) 关联 sys_user_role_rel → sys_role_permission → sys_permission
 *   - 返回结构:
 *       menu:    JSONArray  菜单树 (含 id / name / path / route / component / children / meta.permissionList)
 *                           烘干厂菜单 id = DRY_HOME / DRY_ENTRY / DRY_EXIT / ... / DRY_SYS_*
 *       auth:    JSONArray  按钮权限 (用户拥有的, menu_type=2)
 *       allAuth: JSONArray  全部按钮权限配置 (鉴权时用来判断哪些按钮是受控的)
 *       sysSafeMode: boolean
 *
 * 业务层使用方式:
 *   1) 仅取 auth (按钮权限), 不再用于 permKeys (permKeys 由 getDryingMenuByToken 独占)
 *   2) MainLayout 用 permKeys 过滤 ALL_MENU
 *
 * @returns {Promise<{menu: any[], auth: any[], allAuth: any[], sysSafeMode: boolean}>}
 */
export function getUserPermissionByToken() {
  return request.get('/jeecg-system/sys/permission/getUserPermissionByToken')
}

/**
 * 拉取当前登录用户的烘干厂菜单 (异步加载, 解耦登录)
 *
 * 登录响应不再携带菜单数据 (瘦登录态), 登录成功后调本接口拿菜单.
 * 只返回 DRY_ 前缀、非按钮的权限 id 列表.
 *
 * [2026-06-18] 原 /jeecg-system/sys/permission/getDryingMenuByToken 接口随三角色改造回退
 * 已删除 (hl-drying 单包自包含约束). 改调 hl-drying 自建接口 /drying/menu/myMenus,
 * 后端按 login_acct 查 SYS_PERMISSION, 绕开 jeecg-system getUserPermissionByToken 的已知 bug.
 *
 * @returns {Promise<Array<{id: string}>>} 菜单 id 数组 (DRY_* 前缀)
 */
export function getDryingMenuByToken() {
  return request.get('/hl-drying/drying/menu/myMenus', { timeout: 8000 })
}

/**
 * 修改密码
 * @param {Object} data
 * @param {string} data.oldPassword
 * @param {string} data.newPassword
 */
export function changePassword(data) {
  return request.post('/jeecg-system/sys/user/change-password', data)
}

/**
 * 申请找回 / 重置密码 (匿名)
 * 后端不论 username 是否存在都返回成功, 前端按成功处理 (防枚举).
 * @param {Object} data
 * @param {string} data.username
 * @param {string} data.phone
 * @param {string} [data.remark]
 */
export function pwdResetRequest(data) {
  return request.post('/jeecg-system/sys/pwd-reset-request', data)
}

/**
 * 退出登录
 * 即使后端 logout 调用失败, 前端也视为成功 (清本地状态即可)
 */
export function logout() {
  return request.post('/jeecg-system/sys/logout').catch(() => Promise.resolve())
}
